import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  GraduationCap,
  School,
  Wallet,
  Receipt,
  ClipboardCheck,
  Users,
  Building2,
  Network,
  CheckCircle2,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Wallet,
    title: 'Fund accounting native to coding',
    detail:
      'Title I, IDEA Part B, ESSER (ESSER I/II/III), Perkins V, local bond, general fund, capital fund — coding rules enforce fund-source restrictions, allowable cost categories, and period-of-availability windows at the invoice line, not after posting.',
  },
  {
    icon: Receipt,
    title: 'District tax handling',
    detail:
      'District-level sales / use tax exemptions, vendor 1099 reporting, p-card reconciliation flows, and the distinction between food-service-fund tax treatment and general-fund tax treatment are wired into coding and validation.',
  },
  {
    icon: ClipboardCheck,
    title: 'Encumbrance + in-workflow receiving',
    detail:
      'Purchase orders encumber funds in Oracle EBS at issue; receiving is performed by school staff (principals, secretaries, department heads) directly in the AP workflow at the point of approval — not as a separate EBS receiving transaction.',
  },
  {
    icon: School,
    title: 'Multi-school coding',
    detail:
      'Invoice line items are coded to school / department / location segments with school-specific budget owners as approvers. One vendor invoice can split across 5+ schools with school-specific account strings.',
  },
];

const SCENARIOS = [
  {
    icon: Wallet,
    title: 'ESSER funding wind-down',
    body:
      'Districts that scaled coding complexity to absorb ESSER I/II/III now have layered fund-source rules that must continue tracking allowable costs and period-of-availability through liquidation. AP automation has to carry the coding logic forward as the federal funds wind down, not lose it.',
  },
  {
    icon: Users,
    title: 'New CFO inheriting decade-old WebCenter AP',
    body:
      'A new finance leader arrives, finds a WebCenter AP implementation that has been running for 8-12 years, original implementers gone, no current documentation, and a December 2026 Premier Support cliff approaching. Decision needed in their first budget cycle.',
  },
  {
    icon: Network,
    title: 'EBS to Fusion migration in flight',
    body:
      'District has committed to an EBS-to-Fusion ERP migration and the existing WebCenter AP layer is one of the moving pieces. Question on the table is whether AP modernizes alongside the ERP migration or separately, and whether WebCenter carries forward at all.',
  },
  {
    icon: Building2,
    title: 'Decentralized purchasing, central AP',
    body:
      'Principals and department heads at individual schools initiate purchases against their school-specific budgets. Central AP handles invoice intake, coding validation, and payment. AP automation has to give school-level approvers a workflow they can use without being EBS-trained.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    label: 'Tier 1 · K-12 AP Decision Call',
    title: 'Fixed-fee diagnostic for K-12 finance leaders',
    body:
      'A structured 30-minute Decision Call with Andrew to read your current AP stack — EBS or Fusion, WebCenter footprint, fund accounting model, school count, and procurement-system integration. Output is a written summary of the forward paths available to your district.',
  },
  {
    label: 'Tier 2 · WebCenter + EBS Inventory',
    title: 'One-week read of your existing implementation',
    body:
      'Andrew reads your running WebCenter AP and EBS configuration directly — Forms Recognition rules, SOA composites, ADF customizations, AME approval routing, multi-school coding logic, fund-source validation. Output is a structured inventory document used as input to the modernization or upgrade decision.',
  },
  {
    label: 'Tier 3 · K-12 Modernization Advisory',
    title: 'Founder-level guidance through the procurement cycle',
    body:
      'Monthly retainer with Andrew, sized for districts moving through a 6-18 month board-approved procurement timeline. Strategic guidance on RFP scoping, vendor evaluation, fund-accounting requirements drafting, and Oracle-stack tradeoffs — independent of which delivery partner the district ultimately selects.',
  },
  {
    label: 'Tier 4 · Forward Deployed Engineering',
    title: 'Embedded delivery for the district',
    body:
      'When the district selects EZ Cloud, Forward Deployed Engineering puts a founder-led delivery team inside the district AP function for the implementation window. Fund-accounting coding rules, school-level approval workflows, encumbrance flows, and EBS or Fusion integration are configured directly with district staff. WCPSS-pattern delivery.',
  },
];

const FAQS = [
  {
    q: 'Does the platform handle fund accounting and district-level tax requirements?',
    a: 'Yes — fund accounting and district-level tax handling are native to the coding layer, not bolted on. Coding rules enforce fund-source restrictions at the invoice line, including Title I, IDEA Part B, ESSER I/II/III, Perkins V, local bond, general fund, and capital fund. District-level sales / use tax exemption handling is part of the standard validation logic, including the distinction between food-service-fund tax treatment and general-fund tax treatment. Coding is enforced before the invoice posts to Oracle EBS or Fusion — not corrected after posting.',
  },
  {
    q: 'Can the platform track ESSER, IDEA, and Title I allowable costs and period of availability?',
    a: 'Yes — allowable cost categories, supplement-not-supplant logic, and period-of-availability windows are configured per fund and enforced at coding time. ESSER I, II, and III each have their own liquidation windows and allowable cost lists, and the workflow blocks coding combinations that would create audit exposure. The same pattern extends to IDEA Part B, Perkins V, and locally restricted funds.',
  },
  {
    q: 'How are encumbrances handled?',
    a: 'Purchase orders encumber funds in Oracle EBS or Fusion at the point of issue, and the platform releases the encumbrance and posts the actual expense when the matching invoice arrives. The invoice matches against the encumbered PO and posts against the same fund and account string. Mismatch handling — invoice exceeds PO, partial receipt, multi-PO invoice — is configured into the workflow rather than left as an EBS-side exception.',
  },
  {
    q: 'Can principals and school staff do receiving inside the AP workflow?',
    a: 'Yes — receiving is performed at the school by the person who actually received the goods or services, directly inside the AP approval workflow on their phone or browser. Principals, school secretaries, department heads, or designated receivers confirm receipt without an EBS login and without a separate receiving transaction in EBS. The receipt confirmation is recorded as part of the approval chain and posted back to EBS as a matched receipt.',
  },
  {
    q: 'How does multi-school coding work for a single vendor invoice?',
    a: 'A single vendor invoice can be split across multiple schools, departments, and account strings at the line level, with each line routed to the appropriate school-level approver. Routing to principals, department heads, or designated budget owners is driven by the chart segments and dollar thresholds configured for that school. The approval chain runs in parallel where possible, and the consolidated approved invoice posts as a single multi-line distribution to EBS or Fusion.',
  },
  {
    q: 'How does the procurement and board-approval cycle factor in?',
    a: 'K-12 districts typically run a board-approved RFP cycle with multi-year contract terms, and decisions move on the district fiscal calendar rather than the vendor sales calendar. The Decision Call and Inventory tiers are designed to fit inside the pre-RFP discovery window so finance leaders can scope the AP modernization requirement properly before procurement opens. EZ Cloud responds to formal RFPs and is comfortable with board-presentation materials, references, and the contracting cadence districts require.',
  },
  {
    q: 'Is EZ Cloud connected to the Council of the Great City Schools?',
    a: 'Yes — EZ Cloud is part of the Council of the Great City Schools (CGCS), the network of the nation\'s largest urban public school districts. The CGCS connection is a peer-network credibility anchor for large district finance and IT leaders — it signals that we operate inside the K-12 finance community rather than as a generic ERP-adjacent vendor. We are comfortable being referenced inside CGCS finance and CIO peer conversations.',
  },
  {
    q: 'Is there a K-12 reference deployment we can talk to?',
    a: 'Yes — Wake County Public School System (WCPSS), one of the largest K-12 districts in the United States, runs EZ Cloud in production on Oracle EBS across roughly 25 schools and departments. The deployment covers multi-school coding, fund-source validation, school-level approval routing, and encumbrance flows. The reference is available to qualified district finance leaders on a Decision Call basis. See the case study at /customers/school-district-oracle-ebs.',
  },
];

const WebCenterK12 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter for K-12 School Districts — AP Modernization on Oracle EBS | EZ Cloud',
    description:
      'Oracle WebCenter AP modernization for K-12 school districts. Fund accounting, district tax, encumbrance, in-workflow receiving, multi-school coding. WCPSS-style production deployment on Oracle EBS with the December 2026 12c support cliff in view.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-k12',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter K-12' },
      { '@type': 'Thing', name: 'K-12 School District AP Automation' },
      { '@type': 'Thing', name: 'Fund Accounting' },
      { '@type': 'Thing', name: 'Oracle EBS K-12' },
      { '@type': 'Thing', name: 'WCPSS' },
      { '@type': 'Thing', name: 'Council of the Great City Schools' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter for K-12 School Districts — AP Modernization on Oracle EBS | EZ Cloud"
        description="Oracle WebCenter AP modernization for K-12 school districts. Fund accounting, district tax, encumbrance, in-workflow receiving, multi-school coding. WCPSS-style production deployment on Oracle EBS with the December 2026 12c support cliff in view."
        canonical="https://ezcloud.ai/solutions/webcenter-k12"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter for K-12',
            url: 'https://ezcloud.ai/solutions/webcenter-k12',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <GraduationCap className="w-4 h-4" aria-hidden />
              K-12 Districts · Oracle EBS WebCenter AP
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter for K-12 school districts
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Production-grade AP automation for K-12 districts on Oracle EBS. Fund accounting,
                district tax, encumbrance, in-workflow receiving.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              The December 2026 forward path for the K-12 lifer CFO / CIO. Built on the same WCPSS
              production pattern — multi-school coding, fund-source validation, school-level
              approval routing, and encumbrance flows on Oracle EBS.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-k12-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a K-12 Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              WebCenter AP modernization for K-12 districts on Oracle EBS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  EZ Cloud delivers AP automation purpose-built for K-12 school districts running
                  Oracle E-Business Suite — with the same production pattern proven at Wake County
                  Public School System (WCPSS).
                </strong>{' '}
                Stable, multi-school AP automation with fund accounting and district tax built in —
                not bolted on after implementation.
              </p>
              <p>
                Fund-source validation (Title I, IDEA, ESSER, Perkins V, local bond), district tax
                handling, encumbrance flows, in-workflow receiving by school staff, multi-school
                coding splits, and school-level approval routing are configured into the standard
                workflow. Oracle WebCenter Forms Recognition, Imaging, and Capture customers
                modernize forward onto a platform built for the way district AP actually runs.
              </p>
              <p>
                The December 2026 Oracle Fusion Middleware 12c Premier Support cliff is the forcing
                function. The K-12 lifer CFO / CIO who has owned the AP stack for a decade is the
                buyer, and the board-approved procurement cycle is the timeline. EZ Cloud is part of
                the Council of the Great City Schools and engages on the K-12 finance calendar.
              </p>
            </div>
          </div>
        </section>

        {/* K-12 AP-specific requirements */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The K-12 AP requirement set
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four AP requirements specific to K-12 districts
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              K-12 AP is not generic enterprise AP with a school logo on it. Fund accounting,
              district tax, encumbrance, and multi-school coding are first-class requirements that
              the platform either handles natively or gets wrong.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Where K-12 WebCenter customers are today */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Where K-12 WebCenter customers are today
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Three common K-12 starting points
            </h2>
            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">Most large districts are on Oracle EBS R12.</strong>{' '}
                R12 has been the K-12 finance backbone for over a decade. Many districts layered
                WebCenter Forms Recognition, WebCenter Imaging, and AME-driven approval workflows on
                top of EBS Payables to handle the volume and coding complexity that PeopleSoft-era
                AP simply could not absorb.
              </p>
              <p>
                <strong className="text-gray-900">
                  A growing minority are on Fusion Cloud ERP or in flight toward it.
                </strong>{' '}
                Districts that have migrated or are migrating to Oracle Fusion Cloud ERP face the
                additional question of whether their WebCenter AP layer carries forward, gets
                replaced inside Fusion, or modernizes onto a separate AP platform that integrates
                via Oracle Integration Cloud.
              </p>
              <p>
                <strong className="text-gray-900">
                  Procurement systems run alongside — Frontline, Munis, Tyler, and others.
                </strong>{' '}
                K-12 districts often run a separate procurement / requisition platform that
                integrates with EBS or Fusion for the PO and encumbrance. AP modernization has to
                integrate cleanly with that procurement system rather than replace it.
              </p>
              <p>
                <strong className="text-gray-900">
                  And in many districts, there is a shadow WebCenter footprint nobody has touched
                  in 10 years.
                </strong>{' '}
                Forms Recognition rules, ADF customizations on the non-PO coding form, custom SOA
                composites for AME routing — implemented by people who have since left, documented
                in a SharePoint folder that nobody can find. The December 2026 12c Premier Support
                cliff brings this forward as a decision the current finance and IT leadership has to
                make.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/solutions/education-oracle-ebs"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Education on Oracle EBS
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
              <Link
                to="/industries/education"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Education industry overview
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* CGCS angle */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-accent" aria-hidden />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Peer network credibility
                </p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  EZ Cloud is part of the Council of the Great City Schools
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  The Council of the Great City Schools (CGCS) is the coalition of the nation's
                  largest urban public school districts. Membership means EZ Cloud operates inside
                  the K-12 finance peer network — CFO, CIO, and finance director conversations
                  happen on the inside of the CGCS network rather than from outside it. For large
                  urban district finance leaders, the CGCS connection signals that we know how
                  district AP actually runs, not just how to sell to it.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WCPSS reference */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Reference deployment
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wake County Public School System — production on Oracle EBS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Wake County Public School System (WCPSS) — one of the largest K-12 districts in the
                United States — runs EZ Cloud in production on Oracle E-Business Suite across
                roughly 25 schools and departments. The deployment carries multi-school coding,
                fund-source validation, encumbrance flows, and school-level approval routing — the
                full K-12 AP requirement set.
              </p>
              <p>
                WCPSS is the canonical K-12 reference pattern. The same multi-school coding model,
                AME-driven approval routing, and EBS Payables integration approach used at WCPSS is
                the starting template for new K-12 district engagements. Reference access is
                available to qualified district finance leaders on a Decision Call basis.
              </p>
            </div>
            <div className="mt-6">
              <Link
                to="/customers/school-district-oracle-ebs"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                Read the K-12 school district case study
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* K-12 procurement cycle */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              How K-12 districts buy
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The K-12 procurement cycle is its own calendar
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                K-12 districts move on board-approved procurement timelines. Most large district AP
                modernizations run through a formal RFP cycle, typically 6-18 months from
                pre-discovery through board approval and contract execution. Contract terms are
                often multi-year, with renewal points aligned to the district fiscal calendar.
              </p>
              <p>
                The Decision Call is the first step. It is not a sales call — it is the
                pre-discovery conversation that lets the district finance leader scope the AP
                modernization properly before the RFP opens. Inventory and Advisory tiers fit
                inside the pre-RFP window. Forward Deployed Engineering kicks in once the district
                has selected EZ Cloud and the contract is in place.
              </p>
              <p>
                We are comfortable with board-presentation materials, formal RFP responses,
                reference checks through CGCS peer networks, and the contracting cadence districts
                require. We do not push commercial timelines onto a district's fiscal calendar.
              </p>
            </div>
          </div>
        </section>

        {/* Common K-12 scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What we hear from K-12 finance and IT leaders
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four common K-12 scenarios
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The conversations that show up most often with district CFOs, finance directors, and
              CIOs as the December 2026 12c support cliff approaches.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages with K-12 districts
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for K-12 AP modernization
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Standard consulting tiers fit pre-RFP discovery and inventory work. Forward Deployed
              Engineering is the delivery mode once the district has selected EZ Cloud and the
              contract is executed.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((tier) => (
                <Card key={tier.label} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {tier.label}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{tier.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{tier.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call and adjusted for
              district fiscal-year cadence.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the 12c support cliff — including
              the K-12-specific considerations around fund accounting, multi-school coding, and the
              board-approved procurement cycle.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers for K-12 finance and IT leaders
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your K-12 AP modernization
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist. Walk through your district's AP stack, fund-accounting
              model, and procurement timeline — and the WCPSS-pattern forward path in the context
              of your specific implementation.
            </p>
            <div className="flex items-center justify-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-6">
              <CheckCircle2 className="w-4 h-4" aria-hidden />
              Council of the Great City Schools member
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-k12-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book a K-12 Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterK12;
